import React, { memo, useMemo } from 'react'

import { array, arrayOf, shape, string, bool } from 'prop-types'

import i18n from 'Pods/Shared/I18n'
import CategoryMegaMenu from 'Pods/UnifiedNavigation/Components/Navigation/Shared/CategoryMegaMenu/CategoryMegaMenu'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

// Redesigned 3D Models mega-menu panel. Feeds the shared CategoryMegaMenu the same
// backend data the legacy Assets3DCategories panel received (asset categories, types,
// budgets, actions). The wrapping `.models-3d-dropdown` element carries the panel id
// and is the visibility hook: statically imported and SSR-rendered (MenuItem
// persistPanel) so its links ship in the initial HTML and stay crawlable, then shown
// via the `.is-open` reveal in _unified-navigation.scss.
function Models3DDropdown({
  id = undefined,
  assetCategories,
  byType,
  locale = 'en',
  budgets = EMPTY_ARRAY,
  baseUrl,
  actionsConfig,
}) {
  const translations = useMemo(() => {
    const t = (key) => i18n.t(key, { ns: 'rails', lng: locale })

    return {
      categoriesTitle: t('navigation.navbar.3d_models.categories'),
      typesTitle: t('navigation.navbar.3d_models.types'),
      budgetTitle: t('navigation.navbar.3d_models.budget'),
      // "All 3D Models" header link label (distinct from the footer's "All models" CTA).
      browseAll: t('navigation.navbar.3d_models.browse_all'),
      actions: {
        new: t('navigation.navbar.actions.new'),
        trending: t('navigation.navbar.actions.trending'),
        topSelling: t('navigation.navbar.actions.top_selling'),
        allModels: t('navigation.navbar.3d_models.all_models'),
        // Mobile-only footer link to the category root (distinct from the listing).
        about: t('navigation.navbar.3d_models.about'),
      },
    }
  }, [locale])

  // Subscription budgets need the flag gate AND the viewer-specific selection: the backend
  // ships two subscription entries (the withSub:false upsell and the withSub:true subscriber
  // filter), and only the one matching the current viewer should render - mirroring the
  // legacy BudgetList. Non-subscription budgets always pass through.
  const visibleBudgets = useMemo(() => {
    const subscriptionEnabled = Boolean(
      actionsConfig?.subscriptionMenuEnabled && actionsConfig?.subscriptionFlowEnabled
    )

    return budgets.filter((budget) => {
      if (budget.icon !== 'subscription') {
        return true
      }

      return subscriptionEnabled && budget.withSub === actionsConfig?.isActiveSubscriber
    })
  }, [budgets, actionsConfig])

  return (
    <div className="models-3d-dropdown" id={id}>
      <CategoryMegaMenu
        categories={assetCategories}
        types={byType}
        budgets={visibleBudgets}
        baseUrl={baseUrl}
        browseAllHref={`${baseUrl}?page=1`}
        translations={translations}
      />
    </div>
  )
}

Models3DDropdown.propTypes = {
  id: string,
  assetCategories: array.isRequired,
  byType: arrayOf(shape({
    icon: string,
    name: string,
    path: string,
  })).isRequired,
  locale: string,
  budgets: arrayOf(shape({
    title: string.isRequired,
    path: string.isRequired,
    icon: string.isRequired,
    withSub: bool,
  })),
  baseUrl: string.isRequired,
  actionsConfig: shape({
    subscriptionMenuEnabled: bool,
    subscriptionFlowEnabled: bool,
    isActiveSubscriber: bool,
  }).isRequired,
}

export default memo(Models3DDropdown)
